import { Link } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import {
  formatIllumination,
  groupNightsByMonth,
  quarterEnd,
  QUARTER_DAYS,
  showerPeakWindows,
  upcomingNights,
} from "../../lib/windows";
import { nextNewMoon } from "../../lib/moon";
import type { NightWindow } from "../../types";

const Windows = () => {
  const nights = upcomingNights(QUARTER_DAYS);
  const peaks = showerPeakWindows(new Date(), QUARTER_DAYS);
  const newMoon = nextNewMoon(new Date());
  const darkCount = nights.filter((n) => n.quality === "dark").length;
  const through = quarterEnd();
  const months = groupNightsByMonth(nights);
  const newMoonInWindow = newMoon.getTime() <= through.getTime();

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 py-12 w-full">
        <p className="text-sun font-semibold uppercase tracking-wide text-sm">
          Next quarter
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-alt-blk mt-3">
          Upcoming good nights
        </h1>
        <p className="text-xl text-alt-blk/80 mt-4 max-w-3xl leading-relaxed">
          Through{" "}
          {through.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
          })}
          . New-moon windows first. Meteor showers when the moon cooperates.
          Lunar geometry, not a weather forecast — still pack for more than one
          night.
        </p>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <div className="rounded-2xl bg-white border border-alt-blk/10 p-5">
            <div className="text-xs uppercase tracking-wide text-alt-blk/50">
              Next new moon
            </div>
            <div className="text-2xl font-bold mt-1">
              {newMoon.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </div>
            {!newMoonInWindow && (
              <div className="text-xs text-alt-blk/50 mt-1">After this window</div>
            )}
          </div>
          <div className="rounded-2xl bg-white border border-alt-blk/10 p-5">
            <div className="text-xs uppercase tracking-wide text-alt-blk/50">
              Dark nights (≤18% lit)
            </div>
            <div className="text-2xl font-bold mt-1">
              {darkCount} of {QUARTER_DAYS}
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-alt-blk/10 p-5">
            <div className="text-xs uppercase tracking-wide text-alt-blk/50">
              Next shower peak
            </div>
            <div className="text-2xl font-bold mt-1">
              {peaks[0]
                ? `${peaks[0].shower.name} ${formatNight(peaks[0].peak)}`
                : "—"}
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold mt-12 mb-4">Meteor showers</h2>
        {peaks.length === 0 ? (
          <p className="text-alt-blk/70">
            No major shower peaks in this quarter. The nights below still mark
            the dark-moon stretch.
          </p>
        ) : (
          <ul className="space-y-3">
            {peaks.map(({ shower, peak, favorable }) => (
              <li
                key={`${shower.id}-${peak.date}`}
                className="rounded-2xl bg-white border border-alt-blk/10 p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
              >
                <div>
                  <div className="font-bold text-lg">{shower.name}</div>
                  <p className="text-sm text-alt-blk/70 mt-1">{shower.notes}</p>
                </div>
                <div className="text-sm sm:text-right shrink-0">
                  <div className="font-semibold">
                    {formatNight(peak)} · ~{shower.zhr}/hr
                  </div>
                  <div className={favorable ? "text-turquoise" : "text-mars"}>
                    {favorable
                      ? `Favorable moon · ${formatIllumination(peak.illumination)}`
                      : `Bright moon · ${formatIllumination(peak.illumination)}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <h2 className="text-2xl font-bold mt-12 mb-4">Night-by-night</h2>
        {months.map((month) => (
          <div key={month.label} className="mt-6">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-alt-blk/50 mb-3">
              {month.label}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {month.nights.map((night) => (
                <div
                  key={night.date}
                  className={`rounded-2xl p-3 border text-sm ${tone(night.quality)}`}
                >
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold">
                      {night.weekday} {night.date.slice(8)}
                    </span>
                    <span className="text-xs opacity-80">
                      {Math.round(night.illumination * 100)}%
                    </span>
                  </div>
                  <div className="text-xs mt-1 opacity-80">{night.phaseName}</div>
                  {night.showers.slice(0, 1).map((shower) => (
                    <div key={shower.id} className="text-xs mt-2 font-medium">
                      {shower.name}
                      {shower.isPeak ? " peak" : ""}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}

        <p className="text-sm text-alt-blk/60 mt-8">
          Moon illumination is a simple synodic estimate, good enough to plan a
          trip. Shower nights follow the AMS working list through 2027.{" "}
          <Link to="/" className="text-turquoise hover:text-mars">
            Pick a stay-ready site
          </Link>{" "}
          while the window is open.
        </p>
      </section>
    </Layout>
  );
};

function tone(quality: NightWindow["quality"]): string {
  if (quality === "dark") return "bg-alt-blk text-alt-wht border-alt-blk";
  if (quality === "fair") return "bg-foam/20 border-foam/40 text-alt-blk";
  return "bg-white border-alt-blk/10 text-alt-blk/70";
}

function formatNight(night: NightWindow): string {
  const [y, m, d] = night.date.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export default Windows;
